import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Trash,
  AlertCircle,
  Loader2,
  RefreshCw,
  Database,
  CheckCircle,
  Clock,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../api/api';
import { Badge } from '../ui/badge';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SystemManager() {
  const { isDarkMode } = useTheme();
  
  // Theme-aware classes
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  const [clearingCache, setClearingCache] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchCacheInfo();
    fetchStats();
  }, []);

  const fetchCacheInfo = async () => {
    try {
      const info = await adminApi.getCacheVersion();
      setCacheInfo(info);
    } catch (err) {
      console.error('Failed to fetch cache info:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await adminApi.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const result = await adminApi.clearSiteCache();
      setCacheInfo({ version: result.new_version, cleared_at: result.cleared_at, cleared_by: result.cleared_by });
      toast.success('Site cache cleared successfully!', {
        description: `All users will refresh to version ${result.new_version} on their next visit.`
      });
    } catch (error) {
      toast.error('Failed to clear cache', {
        description: error.response?.data?.detail || 'Unknown error'
      });
    } finally {
      setClearingCache(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Cache Management */}
      <Card className={`${bgSecondary} ${borderColor}`}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Trash className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <CardTitle className={textPrimary}>Clear Site Cache</CardTitle>
              <CardDescription className={textMuted}>
                Force all user devices to refresh cached content
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className={`p-4 ${bgTertiary} rounded-lg`}>
            <p className={`${textSecondary} mb-3`}>
              This will increment the cache version, causing all users&apos; browsers to:
            </p>
            <ul className={`list-disc list-inside space-y-1 text-sm ${textMuted}`}>
              <li>Clear their localStorage cache on next visit</li>
              <li>Clear Service Worker caches (if PWA installed)</li>
              <li>Force re-download of updated assets</li>
            </ul>
          </div>

          {cacheInfo && (
            <div className={`p-4 ${bgTertiary} rounded-lg border ${borderColor}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textMuted}`}>Current Cache Version</p>
                  <p className={`text-2xl font-bold ${textPrimary}`}>v{cacheInfo.version}</p>
                </div>
                {cacheInfo.cleared_at && (
                  <div className="text-right">
                    <p className={`text-sm ${textMuted}`}>Last Cleared</p>
                    <p className={textSecondary}>
                      {new Date(cacheInfo.cleared_at).toLocaleDateString()} at {new Date(cacheInfo.cleared_at).toLocaleTimeString()}
                    </p>
                    {cacheInfo.cleared_by && (
                      <p className={`text-xs ${textMuted}`}>by {cacheInfo.cleared_by}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <Button
              onClick={handleClearCache}
              disabled={clearingCache}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="clear-cache-btn"
            >
              {clearingCache ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing Cache...
                </>
              ) : (
                <>
                  <Trash className="w-4 h-4 mr-2" />
                  Clear All Site Cache
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={fetchCacheInfo}
              className={`${borderColor} ${textMuted}`}
              data-testid="refresh-cache-info-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Info
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card className={`${bgSecondary} ${borderColor}`}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className={textPrimary}>System Information</CardTitle>
              <CardDescription className={textMuted}>
                Application and server details
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 ${bgTertiary} rounded-lg`}>
              <p className={`text-sm ${textMuted}`}>Application</p>
              <p className={`font-medium ${textPrimary}`}>MyPatriotMail</p>
            </div>
            <div className={`p-4 ${bgTertiary} rounded-lg`}>
              <p className={`text-sm ${textMuted}`}>Environment</p>
              <p className={`font-medium ${textPrimary}`}>Production</p>
            </div>
            <div className={`p-4 ${bgTertiary} rounded-lg`}>
              <p className={`text-sm ${textMuted}`}>Backend URL</p>
              <p className={`font-medium ${textPrimary} text-xs break-all`}>{process.env.REACT_APP_BACKEND_URL}</p>
            </div>
            <div className={`p-4 ${bgTertiary} rounded-lg`}>
              <p className={`text-sm ${textMuted}`}>Total Users</p>
              <p className={`font-medium ${textPrimary}`}>{stats.total_users || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Backups */}
      <BackupSection bgSecondary={bgSecondary} bgTertiary={bgTertiary} borderColor={borderColor} textPrimary={textPrimary} textMuted={textMuted} />
    </div>
  );
}

function BackupSection({ bgSecondary, bgTertiary, borderColor, textPrimary, textMuted }) {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/migration/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/migration/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `mypatriotmail_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded');
    } catch (err) {
      toast.error('Download failed');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/migration/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(response.data);
      toast.success(`Restored: ${response.data.total_imported} records`);
      loadStats();
    } catch (err) {
      toast.error('Upload failed');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Card className={`${bgSecondary} ${borderColor}`}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Database className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <CardTitle className={textPrimary}>Database Backup & Restore</CardTitle>
            <CardDescription className={textMuted}>
              Download your data or restore from a backup
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        {stats && (
          <div className={`p-4 ${bgTertiary} rounded-lg`}>
            <div className="flex justify-between items-center">
              <span className={textMuted}>Total Documents:</span>
              <span className={`font-bold ${textPrimary}`}>{stats.total_documents}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className={textMuted}>Collections:</span>
              <span className={`font-bold ${textPrimary}`}>{Object.keys(stats.collections || {}).length}</span>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={downloadBackup} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700">
            {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</> : <><Download className="w-4 h-4 mr-2" /> Download Backup</>}
          </Button>
          
          <input type="file" accept=".json" onChange={handleUpload} className="hidden" id="restore-file" disabled={uploading} />
          <label htmlFor="restore-file">
            <Button asChild variant="outline" className={`${borderColor} cursor-pointer`} disabled={uploading}>
              <span>
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Restore from Backup</>}
              </span>
            </Button>
          </label>
          
          <Button variant="ghost" onClick={loadStats} className={textMuted} size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className={`p-4 ${bgTertiary} rounded-lg border border-emerald-500/30`}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className={`font-medium ${textPrimary}`}>Restore Complete</span>
            </div>
            <p className={`text-sm ${textMuted}`}>
              Imported: {uploadResult.total_imported} | Skipped: {uploadResult.total_skipped}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
